import { Box, Flex } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useElementSize } from "@mantine/hooks";
import { useApiUrl, useCustom } from "@refinedev/core";
import * as d3 from "d3";
import { endOfYear, formatISO, parseJSON, startOfYear, format } from "date-fns";
import { useEffect, useRef, useState } from "react";

type TeaAddon = {
  box_month: string;
  started_at: Date;
  ended_at: Date;
  shipped_at?: Date;
};

type TeaAddonSubscription = {
  started_at: Date;
  canceled_at?: Date;
};

type Box = {
  box_name: string;
  from: Date;
  to: Date;
  extension_start: Date | null;
  extension_end: Date | null;
};

type OMSOrder = {
  box_name: string;
  from: Date;
  to: Date;
  shipped_at?: Date;
  after_renewal: boolean;
};

type Data = {
  subscription_plan: string;
  subscription_started_at: Date;
  subscription_canceled_at?: Date;
  subscription_renewals: Date[];
  boxes: Box[];
  orders: OMSOrder[];
  tea_addon_subscriptions: TeaAddonSubscription[];
  tea_addons: TeaAddon[];
};

const colorsByMonth: Record<string, string> = {
  January: "#4CAF50",
  February: "#FF9800",
  March: "#03A9F4",
  April: "#9C27B0",
  May: "#8BC34A",
  June: "#F44336",
  July: "#00BCD4",
  August: "#673AB7",
  September: "#E91E63",
  October: "#009688",
  November: "#2196F3",
  December: "#3F51B5",
};

// Tooltip utility class
class Tooltip {
  private tooltip: d3.Selection<HTMLDivElement, unknown, HTMLElement, any>;

  constructor() {
    // Create tooltip div
    this.tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "timeline-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", "rgba(0, 0, 0, 0.9)")
      .style("color", "white")
      .style("padding", "8px 12px")
      .style("border-radius", "6px")
      .style("font-size", "12px")
      .style("font-family", "system-ui, -apple-system, sans-serif")
      .style("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.3)")
      .style("pointer-events", "none")
      .style("z-index", "1000")
      .style("max-width", "250px")
      .style("line-height", "1.4");
  }

  show(content: string, event: MouseEvent) {
    this.tooltip
      .style("visibility", "visible")
      .html(content)
      .style("left", event.pageX + 10 + "px")
      .style("top", event.pageY - 10 + "px");
  }

  hide() {
    this.tooltip.style("visibility", "hidden");
  }

  move(event: MouseEvent) {
    this.tooltip
      .style("left", event.pageX + 10 + "px")
      .style("top", event.pageY - 10 + "px");
  }

  destroy() {
    this.tooltip.remove();
  }
}

function Timeline({ id }: { id: string }) {
  const ref = useRef<SVGSVGElement>(null);
  const { ref: containerRef, width: containerWidth } = useElementSize();
  const [startDate, setStartDate] = useState<Date>(startOfYear(new Date()));
  const [endDate, setEndDate] = useState<Date>(endOfYear(new Date()));
  const tooltipRef = useRef<Tooltip | null>(null);

  const apiUrl = useApiUrl();
  const { data: timelineData } = useCustom<{
    timeline: Data;
  }>({
    url: `${apiUrl}/tea_addon_subscriptions/${id}/timeline?start_date=${formatISO(startDate)}&end_date=${formatISO(endDate)}`,
    method: "get",
    queryOptions: {
      enabled: true,
    },
  });

  const data = parseDates({
    data: timelineData?.data?.timeline as Data | null,
  });

  // Set the dimensions and margins of the graph
  const margin = { top: 30, right: 30, bottom: 70, left: 60 },
    width = (containerWidth || 860) - margin.left - margin.right,
    height = 400 - margin.top - margin.bottom;

  const subscriptionHeight = 40;
  const subscriptionY = 80;
  const subscriptionMarkerEdge = 10;

  // Helper function to add tooltip interactions
  function addTooltipInteractions(
    selection: d3.Selection<any, any, any, any>,
    getTooltipContent: (d: any) => string,
  ) {
    selection
      .style("cursor", "pointer")
      .on("mouseover", function (event: MouseEvent, d: any) {
        if (tooltipRef.current) {
          tooltipRef.current.show(getTooltipContent(d), event);
        }
        d3.select(this).style("opacity", 0.8);
      })
      .on("mousemove", function (event: MouseEvent) {
        if (tooltipRef.current) {
          tooltipRef.current.move(event);
        }
      })
      .on("mouseout", function () {
        if (tooltipRef.current) {
          tooltipRef.current.hide();
        }
        d3.select(this).style("opacity", null);
      });
  }

  function drawSubscription(
    data: Data,
    svg: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: any,
  ) {
    const subscriptionEndDate = data.subscription_canceled_at || endDate;
    const subscriptionGroup = svg.append("g").attr("class", "subscription");

    // Add subscription box with tooltip
    const subscriptionRect = subscriptionGroup
      .append("rect")
      .attr("x", x(data.subscription_started_at))
      .attr("y", subscriptionY)
      .attr("width", x(subscriptionEndDate) - x(data.subscription_started_at))
      .attr("height", subscriptionHeight)
      .attr("fill", "#4E342E")
      .attr("opacity", 0.9)
      .attr("rx", 6)
      .attr("ry", 6)
      .raise();

    // Add tooltip to subscription
    addTooltipInteractions(subscriptionRect, () => {
      const duration = Math.ceil(
        (subscriptionEndDate.getTime() -
          data.subscription_started_at.getTime()) /
          (1000 * 60 * 60 * 24),
      );
      return `
        <strong>${data.subscription_plan}</strong><br/>
        Started: ${format(data.subscription_started_at, "MMM dd, yyyy")}<br/>
        ${
          data.subscription_canceled_at
            ? `Canceled: ${format(data.subscription_canceled_at, "MMM dd, yyyy")}<br/>`
            : "Status: Active<br/>"
        }
        Duration: ${duration} days<br/>
        Renewals: ${data.subscription_renewals.length}
      `;
    });

    // Add subscription label
    subscriptionGroup
      .append("text")
      .attr("class", "label")
      .attr("text-anchor", "middle")
      .attr(
        "x",
        x(data.subscription_started_at) +
          (x(subscriptionEndDate) - x(data.subscription_started_at)) / 2,
      )
      .attr("y", subscriptionY + subscriptionHeight / 2 + 5)
      .attr("font-size", "14px")
      .attr("fill", "white")
      .text(data.subscription_plan);

    // Add start date marker
    subscriptionGroup
      .append("line")
      .attr("x1", x(data.subscription_started_at))
      .attr("y1", subscriptionY - subscriptionMarkerEdge)
      .attr("x2", x(data.subscription_started_at))
      .attr("y2", subscriptionY + subscriptionHeight + subscriptionMarkerEdge)
      .attr("stroke", "#333")
      .attr("stroke-width", 2);

    // Add start date label
    subscriptionGroup
      .append("text")
      .attr("x", x(data.subscription_started_at))
      .attr("y", subscriptionY - subscriptionMarkerEdge - 5)
      .attr("text-anchor", "middle")
      .attr("font-size", "12px")
      .text("Start");

    // Add end date marker and label if subscription is canceled
    if (data.subscription_canceled_at) {
      subscriptionGroup
        .append("line")
        .attr("x1", x(data.subscription_canceled_at))
        .attr("y1", subscriptionY - subscriptionMarkerEdge)
        .attr("x2", x(data.subscription_canceled_at))
        .attr("y2", subscriptionY + subscriptionHeight + subscriptionMarkerEdge)
        .attr("stroke", "#E53935")
        .attr("stroke-width", 2);

      subscriptionGroup
        .append("text")
        .attr("x", x(data.subscription_canceled_at))
        .attr("y", subscriptionY - subscriptionMarkerEdge - 5)
        .attr("text-anchor", "middle")
        .attr("font-size", "12px")
        .attr("fill", "#E53935")
        .text("Canceled");
    }

    // Add the subscription renewal markers with tooltips
    const renewalMarkers = svg
      .append("g")
      .attr("class", "renewal-markers")
      .selectAll(".renewal-marker")
      .data(data.subscription_renewals)
      .enter();

    const renewalLines = renewalMarkers
      .append("line")
      .attr("x1", (d) => x(d))
      .attr("y1", subscriptionY - subscriptionMarkerEdge)
      .attr("x2", (d) => x(d))
      .attr("y2", subscriptionY + subscriptionHeight + subscriptionMarkerEdge)
      .attr("stroke", "#2196F3")
      .attr("stroke-width", 2);

    // Add tooltip to renewal markers
    addTooltipInteractions(renewalLines, (d: Date) => {
      return `
        <strong>Subscription Renewal</strong><br/>
        Date: ${format(d, "MMM dd, yyyy")}
      `;
    });

    // Add text labels for renewals
    renewalMarkers
      .append("text")
      .attr("x", (d) => x(d))
      .attr("y", subscriptionY - subscriptionMarkerEdge - 5)
      .attr("text-anchor", "middle")
      .attr("font-size", "12px")
      .attr("fill", "#2196F3")
      .text(`Renewal`);
  }

  function drawBoxes(data: Data, svg: any, x: any) {
    // Create a group for boxes
    const boxGroup = svg.append("g").attr("class", "boxes");

    // Add box rectangles with tooltips
    const boxRects = boxGroup
      .selectAll(".box-rect")
      .data(data.boxes)
      .enter()
      .append("rect")
      .attr("class", "box-rect")
      .attr("x", (d: Box) => x(d.from))
      .attr("y", 0)
      .attr("width", (d: Box) => x(d.to) - x(d.from))
      .attr("fill", (d: Box) => colorsByMonth[d.box_name.split(" ")[0]])
      .attr("height", height)
      .attr("opacity", 0.4)
      .attr("rx", 0)
      .attr("ry", 0);

    // Add tooltip to boxes
    addTooltipInteractions(boxRects, (d: Box) => {
      const duration = Math.ceil(
        (d.to.getTime() - d.from.getTime()) / (1000 * 60 * 60 * 24),
      );
      return `
        <strong>${d.box_name}</strong><br/>
        From: ${format(d.from, "MMM dd, yyyy")}<br/>
        To: ${format(d.to, "MMM dd, yyyy")}<br/>
        Duration: ${duration} days
        ${
          d.extension_start && d.extension_end
            ? `<br/><br/><strong>Extension:</strong><br/>
          From: ${format(d.extension_start, "MMM dd, yyyy")}<br/>
          To: ${format(d.extension_end, "MMM dd, yyyy")}`
            : ""
        }
      `;
    });

    // Add box labels
    boxGroup
      .selectAll(".box-text")
      .data(data.boxes)
      .enter()
      .append("text")
      .attr("class", "box-text")
      .attr("x", (d: Box) => x(d.from) + (x(d.to) - x(d.from)) / 2)
      .attr("y", subscriptionHeight / 2 + 5)
      .attr("text-anchor", "middle")
      .attr("font-size", "12px")
      .attr("fill", "#FFFFFF")
      .text((d: Box) => d.box_name.split(" ")[0]);

    // Create a group for box extensions
    const boxExtensionsGroup = svg.append("g").attr("class", "box-extensions");

    const extensionRects = boxExtensionsGroup
      .selectAll(".box-extension-rect")
      .data(
        data.boxes.filter((box) => box.extension_start && box.extension_end),
      )
      .enter()
      .append("rect")
      .attr("class", "box-extension-rect")
      .attr("x", (d: Box) => x(d.extension_start))
      .attr("y", subscriptionHeight / 2 + 5)
      .attr("width", (d: Box) => x(d.extension_end) - x(d.extension_start))
      .attr("fill", (d: Box) => colorsByMonth[d.box_name.split(" ")[0]])
      .attr("height", 15)
      .attr("opacity", 0.7)
      .attr("rx", 0)
      .attr("ry", 0);

    // Add tooltip to extensions
    addTooltipInteractions(extensionRects, (d: Box) => {
      const duration = Math.ceil(
        (d.extension_end!.getTime() - d.extension_start!.getTime()) /
          (1000 * 60 * 60 * 24),
      );
      return `
        <strong>${d.box_name} - Extension</strong><br/>
        From: ${format(d.extension_start!, "MMM dd, yyyy")}<br/>
        To: ${format(d.extension_end!, "MMM dd, yyyy")}<br/>
        Duration: ${duration} days
      `;
    });

    // Add box extensions labels
    boxExtensionsGroup
      .selectAll(".box-extension-text")
      .data(
        data.boxes.filter((box) => box.extension_start && box.extension_end),
      )
      .enter()
      .append("text")
      .attr("class", "box-text")
      .attr(
        "x",
        (d: Box) =>
          x(d.extension_start) +
          (x(d.extension_end) - x(d.extension_start)) / 2,
      )
      .attr("y", subscriptionHeight / 2 + 17)
      .attr("text-anchor", "middle")
      .attr("font-size", "8px")
      .attr("fill", "#FFFFFF")
      .text("EXT");
  }

  function drawOrders(data: Data, svg: any, x: any) {
    const orderHeight = subscriptionHeight;
    const orderY = subscriptionY + 45;
    const orderMarkerEdge = 0;

    // Create a group for orders
    const orderGroup = svg.append("g").attr("class", "orders");

    // Add order rectangles with tooltips
    const orderRects = orderGroup
      .selectAll(".order")
      .data(data.orders)
      .enter()
      .append("rect")
      .attr("class", "order")
      .attr("x", (d: OMSOrder) => x(d.from))
      .attr("y", orderY)
      .attr("width", (d: OMSOrder) => x(d.to) - x(d.from))
      .attr("height", orderHeight)
      .attr("fill", (d: OMSOrder) => colorsByMonth[d.box_name?.split(" ")[0]])
      .attr("opacity", (d: OMSOrder) => (d.after_renewal ? 0.7 : 0.9))
      .attr("rx", 3)
      .attr("ry", 3);

    // Add tooltip to orders
    addTooltipInteractions(orderRects, (d: OMSOrder) => {
      const duration = Math.ceil(
        (d.to.getTime() - d.from.getTime()) / (1000 * 60 * 60 * 24),
      );
      return `
        <strong>${d.box_name} Order</strong><br/>
        From: ${format(d.from, "MMM dd, yyyy")}<br/>
        To: ${format(d.to, "MMM dd, yyyy")}<br/>
        Duration: ${duration} days<br/>
        ${d.shipped_at ? `Shipped: ${format(d.shipped_at, "MMM dd, yyyy")}<br/>` : "Not yet shipped<br/>"}
        ${d.after_renewal ? "After renewal" : "Before renewal"}
      `;
    });

    // Add order labels as separate elements
    orderGroup
      .selectAll(".order-text")
      .data(data.orders)
      .enter()
      .append("text")
      .attr("class", "order-text")
      .attr("x", (d: OMSOrder) => x(d.from) + (x(d.to) - x(d.from)) / 2)
      .attr("y", orderY + orderHeight / 2 + 5)
      .attr("text-anchor", "middle")
      .attr("font-size", "12px")
      .attr("fill", "#FFFFFF")
      .text((d: OMSOrder) => d.box_name.split(" ")[0]);

    // Add shipped markers with tooltips
    const shippedLines = orderGroup
      .selectAll(".order-shipped-at-marker")
      .data(data.orders.filter((order) => order.shipped_at))
      .enter()
      .append("line")
      .attr("x1", (d: OMSOrder) => x(d.shipped_at))
      .attr("y1", orderY - orderMarkerEdge)
      .attr("x2", (d: OMSOrder) => x(d.shipped_at))
      .attr("y2", orderY + orderHeight + orderMarkerEdge)
      .attr("stroke", "#000")
      .attr("stroke-width", 1);

    // Add tooltip to shipped markers
    addTooltipInteractions(shippedLines, (d: OMSOrder) => {
      return `
        <strong>${d.box_name} - Shipped</strong><br/>
        Shipped Date: ${format(d.shipped_at!, "MMM dd, yyyy")}
      `;
    });

    // Add shipped labels
    orderGroup
      .selectAll(".order-shipped-at-marker-label")
      .data(data.orders.filter((order) => order.shipped_at))
      .enter()
      .append("text")
      .attr("class", "order-text")
      .attr("x", (d: OMSOrder) => x(d.shipped_at))
      .attr("y", orderY + orderHeight + 10)
      .attr("text-anchor", "middle")
      .attr("font-size", "8px")
      .attr("fill", (d: OMSOrder) => colorsByMonth[d.box_name.split(" ")[0]])
      .text("Shipped");
  }

  function drawAddonSubscription(
    data: Data,
    svg: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: any,
  ) {
    const teaAddonSubscriptionY = subscriptionY + 100;
    const teaAddonSubscriptionHeight = 20;

    data.tea_addon_subscriptions.forEach((teaAddonSubscription) => {
      const subscriptionEndDate = teaAddonSubscription.canceled_at || endDate;

      const addonRect = svg
        .append("rect")
        .attr("x", x(teaAddonSubscription.started_at))
        .attr("y", teaAddonSubscriptionY)
        .attr(
          "width",
          x(subscriptionEndDate) - x(teaAddonSubscription.started_at),
        )
        .attr("height", teaAddonSubscriptionHeight)
        .attr("fill", "#2962FF")
        .attr("opacity", 0.9)
        .attr("rx", 6)
        .attr("ry", 6);

      // Add tooltip to addon subscription
      addTooltipInteractions(addonRect, () => {
        const duration = Math.ceil(
          (subscriptionEndDate.getTime() -
            teaAddonSubscription.started_at.getTime()) /
            (1000 * 60 * 60 * 24),
        );
        return `
          <strong>Tea Addon Subscription</strong><br/>
          Started: ${format(teaAddonSubscription.started_at, "MMM dd, yyyy")}<br/>
          ${
            teaAddonSubscription.canceled_at
              ? `Canceled: ${format(teaAddonSubscription.canceled_at, "MMM dd, yyyy")}<br/>`
              : "Status: Active<br/>"
          }
          Duration: ${duration} days
        `;
      });

      // Add subscription label
      svg
        .append("text")
        .attr("class", "label")
        .attr("text-anchor", "middle")
        .attr(
          "x",
          x(teaAddonSubscription.started_at) +
            (x(subscriptionEndDate) - x(teaAddonSubscription.started_at)) / 2,
        )
        .attr("y", teaAddonSubscriptionY + teaAddonSubscriptionHeight / 2 + 5)
        .attr("font-size", "10px")
        .attr("fill", "white")
        .text("Tea Addon Subscription");
    });
  }

  function drawCurrentTimeLine(
    svg: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: any,
  ) {
    const now = new Date();

    // Add the vertical line for current time
    const currentLine = svg
      .append("line")
      .attr("x1", x(now))
      .attr("y1", 0)
      .attr("x2", x(now))
      .attr("y2", height)
      .attr("stroke", "#FF0000")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "5,5");

    // Add tooltip to current time line
    addTooltipInteractions(currentLine, () => {
      return `
        <strong>Current Time</strong><br/>
        ${format(now, "MMM dd, yyyy HH:mm")}
      `;
    });

    // Add "NOW" label
    svg
      .append("text")
      .attr("x", x(now))
      .attr("y", -10)
      .attr("text-anchor", "middle")
      .attr("font-size", "12px")
      .attr("font-weight", "bold")
      .attr("fill", "#FF0000")
      .text(`NOW (${formatISO(now, { representation: "date" })})`);
  }

  function drawTeaAddons(
    data: Data,
    svg: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: any,
  ) {
    const teaAddonsGroup = svg.append("g").attr("class", "tea-addons");
    const teaAddonsY = subscriptionY + 135;
    const teaAddonsHeight = 30;
    const teaAddons = data.tea_addons;

    // Add tea addon rectangles with tooltips
    const addonRects = teaAddonsGroup
      .selectAll(".tea-addon")
      .data(teaAddons)
      .enter()
      .append("rect")
      .attr("x", (d: TeaAddon) => x(d.started_at))
      .attr("y", teaAddonsY)
      .attr("width", (d: TeaAddon) => x(d.ended_at) - x(d.started_at))
      .attr("height", teaAddonsHeight)
      .attr("fill", (d: TeaAddon) => colorsByMonth[d.box_month.split(" ")[0]])
      .attr("opacity", 0.9)
      .attr("rx", 6)
      .attr("ry", 6)
      .raise();

    // Add tooltip to tea addons
    addTooltipInteractions(addonRects, (d: TeaAddon) => {
      const duration = Math.ceil(
        (d.ended_at.getTime() - d.started_at.getTime()) / (1000 * 60 * 60 * 24),
      );
      return `
        <strong>${d.box_month} Tea Addon</strong><br/>
        Started: ${format(d.started_at, "MMM dd, yyyy")}<br/>
        Ended: ${format(d.ended_at, "MMM dd, yyyy")}<br/>
        Duration: ${duration} days<br/>
        ${d.shipped_at ? `Shipped: ${format(d.shipped_at, "MMM dd, yyyy")}` : "Not yet shipped"}
      `;
    });

    // Add subscription label
    teaAddonsGroup
      .selectAll(".tea-addon-label")
      .data(teaAddons)
      .enter()
      .append("text")
      .attr("class", "label")
      .attr("text-anchor", "middle")
      .attr(
        "x",
        (d: TeaAddon) =>
          x(d.started_at) + (x(d.ended_at) - x(d.started_at)) / 2,
      )
      .attr("y", teaAddonsY + teaAddonsHeight / 2 + 5)
      .attr("font-size", "10px")
      .attr("fill", "white")
      .text((d: TeaAddon) => `${d.box_month.split(" ")[0]} Addon`);

    // Add shipped markers with tooltips
    const shippedLines = teaAddonsGroup
      .selectAll(".tea-addon-start-marker")
      .data(teaAddons.filter((addon) => addon.shipped_at))
      .enter()
      .append("line")
      .attr("x1", (d: TeaAddon) => x(d.shipped_at))
      .attr("y1", teaAddonsY)
      .attr("x2", (d: TeaAddon) => x(d.shipped_at))
      .attr("y2", teaAddonsY + teaAddonsHeight)
      .attr("stroke", "#000")
      .attr("stroke-width", 1);

    // Add tooltip to shipped markers
    addTooltipInteractions(shippedLines, (d: TeaAddon) => {
      return `
        <strong>${d.box_month} Addon - Shipped</strong><br/>
        Shipped Date: ${format(d.shipped_at!, "MMM dd, yyyy")}
      `;
    });

    teaAddonsGroup
      .selectAll(".tea-addon-shipped-label")
      .data(teaAddons.filter((addon) => addon.shipped_at))
      .enter()
      .append("text")
      .attr("class", "tea-addon-text")
      .attr("x", (d: TeaAddon) => x(d.shipped_at))
      .attr("y", (d: TeaAddon, i: number) => {
        return teaAddonsY + (i % 2 === 0 ? teaAddonsHeight + 10 : -2);
      })
      .attr("text-anchor", "middle")
      .attr("font-size", "8px")
      .attr("fill", (d: TeaAddon) => colorsByMonth[d.box_month.split(" ")[0]])
      .text((d: TeaAddon) => `${d.box_month.split(" ")[0]} Shipped`);
  }

  useEffect(() => {
    // Initialize tooltip
    if (!tooltipRef.current) {
      tooltipRef.current = new Tooltip();
    }

    if (!ref.current || !data || !containerWidth) return;

    // Clear any previous SVG to prevent duplicates
    d3.select(ref.current).selectAll("*").remove();

    // Append the svg object
    const svg = d3
      .select(ref.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X axis
    const x = d3.scaleTime().domain([startDate, endDate]).range([0, width]);

    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "translate(-10,0)rotate(-45)")
      .style("text-anchor", "end");

    drawBoxes(data, svg, x);
    drawSubscription(data, svg, x);
    drawOrders(data, svg, x);
    drawAddonSubscription(data, svg, x);
    drawTeaAddons(data, svg, x);
    drawCurrentTimeLine(svg, x);
  }, [startDate, endDate, data, containerWidth]);

  // Cleanup tooltip on unmount
  useEffect(() => {
    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.destroy();
        tooltipRef.current = null;
      }
    };
  }, []);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <Flex gap="xs" justify="center" mb="md">
        <DatePicker
          value={startDate}
          label="Start date"
          onChange={(d) => d && setStartDate(d)}
        />
        <DatePicker
          value={endDate}
          label="End date"
          onChange={(d) => d && setEndDate(d)}
        />
      </Flex>
      <Box style={{ width: "100%", overflowX: "auto" }}>
        <svg
          width={containerWidth || "100%"}
          height={400}
          id="timeline"
          ref={ref}
        />
      </Box>
    </div>
  );
}

function parseDates({ data }: { data: Data | null }): Data | null {
  if (!data) return null;

  return {
    ...data,
    subscription_started_at: parseJSON(data.subscription_started_at),
    subscription_canceled_at: data.subscription_canceled_at
      ? parseJSON(data.subscription_canceled_at)
      : undefined,
    subscription_renewals: (data.subscription_renewals || []).map(parseJSON),
    boxes: data.boxes.map((box) => ({
      ...box,
      from: parseJSON(box.from),
      to: parseJSON(box.to),
      extension_start: box.extension_start
        ? parseJSON(box.extension_start)
        : null,
      extension_end: box.extension_end ? parseJSON(box.extension_end) : null,
    })),
    tea_addon_subscriptions: data.tea_addon_subscriptions.map(
      (teaAddonSubscription) => ({
        ...teaAddonSubscription,
        started_at: parseJSON(teaAddonSubscription.started_at),
        canceled_at: teaAddonSubscription.canceled_at
          ? parseJSON(teaAddonSubscription.canceled_at)
          : undefined,
      }),
    ),
    tea_addons: data.tea_addons.map((teaAddon) => ({
      ...teaAddon,
      started_at: parseJSON(teaAddon.started_at),
      ended_at: parseJSON(teaAddon.ended_at),
      shipped_at: teaAddon.shipped_at
        ? parseJSON(teaAddon.shipped_at)
        : undefined,
    })),

    orders: data.orders.map((order) => ({
      ...order,
      from: parseJSON(order.from),
      to: parseJSON(order.to),
      shipped_at: order.shipped_at ? parseJSON(order.shipped_at) : undefined,
    })),
  };
}

export default Timeline;
